import CheckIcon from "@mui/icons-material/Check";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { EllipsisTooltip } from "../../../components/EllipsisTooltip/EllipsisTooltip";
import { RoleLevel } from "../../../services/api/types/users/users";
import { brand, gray } from "../../../theme/themePrimitives";
import {
  formatRoleLevelSelectLabel,
  ROLE_LEVEL_OPTIONS_DESC,
} from "../../../utils/roleUtils";

/** Matches add-portal modal width (480px) × 0.9 for consistent menu sizing. */
export const MAX_ROLE_LEVEL_SELECT_MENU_MAX_WIDTH_PX = 480 * 0.9;

export type MaxRoleAccessLevelSelectProps = {
  value: RoleLevel | "";
  onChange: (value: RoleLevel | "") => void;
  userLevel?: RoleLevel;
  disabled?: boolean;
  error?: string;
  onClearError?: () => void;
  id?: string;
};

export const MaxRoleAccessLevelSelect: React.FC<
  MaxRoleAccessLevelSelectProps
> = ({
  value,
  onChange,
  userLevel,
  disabled = false,
  error = "",
  onClearError,
  id,
}) => {
  return (
    <Stack spacing={1}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="subtitle2">
          Maximum role access level
          <Box component="span" sx={{ color: `var(--error-700)` }}>
            *
          </Box>
        </Typography>
        <Tooltip
          title="Sets the highest role level that can be created in this portal"
          arrow
          placement="top"
        >
          <HelpOutlineIcon
            sx={{
              fontSize: "16px",
              color: gray[500],
              cursor: "pointer",
            }}
          />
        </Tooltip>
      </Box>
      <FormControl fullWidth size="small" required error={Boolean(error)}>
        <Select<RoleLevel | "">
          id={id ?? "max-role-access-level-select"}
          displayEmpty
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{
            PaperProps: {
              sx: {
                maxWidth: MAX_ROLE_LEVEL_SELECT_MENU_MAX_WIDTH_PX,
              },
            },
            MenuListProps: {
              sx: { maxWidth: "100%" },
            },
          }}
          value={value}
          onChange={(e: SelectChangeEvent<RoleLevel | "">) => {
            onClearError?.();
            const v = e.target.value;
            onChange(v === "" ? "" : (Number(v) as RoleLevel));
          }}
          disabled={
            disabled ||
            (userLevel != null && userLevel < RoleLevel.MAIN_ADMIN)
          }
          renderValue={(selected: RoleLevel | "") => {
            if (selected === "") {
              return (
                <Typography variant="body2" sx={{ color: gray[500] }}>
                  Select maximum access level
                </Typography>
              );
            }
            const label = formatRoleLevelSelectLabel(selected);
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <EllipsisTooltip title={label} maxWidth="100%">
                  <Typography variant="body2" component="span">
                    {label}
                  </Typography>
                </EllipsisTooltip>
              </Box>
            );
          }}
        >
          {ROLE_LEVEL_OPTIONS_DESC.map((level) => (
            <MenuItem
              key={level}
              value={level}
              disabled={
                userLevel != null &&
                userLevel < RoleLevel.SYSTEM_ADMIN &&
                level >= userLevel
              }
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <EllipsisTooltip
                    title={formatRoleLevelSelectLabel(level)}
                    maxWidth="100%"
                  >
                    <Typography variant="body2" component="span">
                      {formatRoleLevelSelectLabel(level)}
                    </Typography>
                  </EllipsisTooltip>
                </Box>
                {value === level && (
                  <CheckIcon
                    sx={{
                      color: brand[600],
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
        {error ? <FormHelperText>{error}</FormHelperText> : null}
      </FormControl>
    </Stack>
  );
};
